import React from "react";
import { Link } from "react-router-dom";
import { Hand, Leaf, Sprout } from "lucide-react";
import PlantImage from "../../components/fancyplants/PlantImage";
import TrustStrip from "../../components/fancyplants/TrustStrip";
import Seo from "../../components/fancyplants/Seo";
import { SITE_IMAGES } from "../../data/siteImages";

const STORY_CARDS = [
  {
    icon: Leaf,
    title: "Niche, not endless",
    text: "A tight, considered range of exotic and unusual plants - chosen, not catalogued.",
    image: SITE_IMAGES.aboutNiche,
    alt: "A compact variegated plant chosen for a curated collection",
  },
  {
    icon: Sprout,
    title: "Right for the climate",
    text: "Everything is picked for how it does in Irish light, damp and cold. No false promises.",
    image: SITE_IMAGES.aboutClimate,
    alt: "A hardy tree fern growing in damp green conditions",
  },
  {
    icon: Hand,
    title: "Packed by a person",
    text: "Your plant is watered, braced and photographed by the same person who grew it.",
    image: SITE_IMAGES.aboutPacked,
    alt: "A plant delivery box ready to be packed",
  },
];

export default function AboutPage() {
  return (
    <div className="pt-24">
      <Seo
        title="About"
        description="Fancy Plants is a one-person glasshouse in the Irish midlands - a small, curated range of exotic plants, packed by hand with advice from the grower."
        path="/about"
      />
      <section className="container-fp grid items-center gap-10 py-12 md:grid-cols-2">
        <div>
          <p className="eyebrow mb-3">Our story</p>
          <h1 className="t-h1">A one-person glasshouse in the midlands.</h1>
          <p className="t-lead mt-5">
            Fancy Plants started where most good things do - a windowsill that got out of hand.
            What began as a few unusual cuttings became a small, deliberately un-corporate plant
            shop with one obsession: exotic, characterful plants that genuinely thrive in an
            Irish home.
          </p>
          <p className="t-lead mt-4">
            We're not trying to out-range the big garden centres. We're the opposite of that -
            a careful, curated few, packed by hand, with advice from someone who actually grew them.
          </p>
        </div>
        <PlantImage
          src={SITE_IMAGES.account}
          alt="A healthy monstera in a bright home"
          ratio="4/5"
          className="rounded-3xl"
        />
      </section>

      <section className="container-fp grid gap-6 py-12 md:grid-cols-3">
        {STORY_CARDS.map(({ icon: Icon, title, text, image, alt }) => (
          <div key={title} className="surface overflow-hidden">
            <PlantImage src={image} alt={alt} ratio="3/2" />
            <div className="p-6">
              <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-forest-100 text-forest-700">
                <Icon size={22} />
              </span>
              <h2 className="font-display text-xl">{title}</h2>
              <p className="mt-2 text-ink-soft">{text}</p>
            </div>
          </div>
        ))}
      </section>

      <TrustStrip tone="dark" />

      <section className="container-fp py-16 text-center">
        <h2 className="t-h2 mx-auto max-w-xl">Got a question about a plant? Just ask.</h2>
        <p className="t-lead mx-auto mt-3 max-w-lg">
          That conversation is the whole point of a small shop. Tell us your room, your light,
          your worries - we'll point you at the right plant.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/shop" className="btn-brass">Browse the shop</Link>
          <Link to="/visit" className="btn-outline">Visit &amp; delivery</Link>
        </div>
      </section>
    </div>
  );
}
